import GroupHeader from "@/components/GroupHeader";
import GroupFooter from "@/components/GroupFooter";
import SEO from "@/components/SEO";

export default function LegalPage({ slug, title, description, sections, lastUpdated }) {
    return (
        <div data-testid={`legal-${slug}`} className="bg-cream text-ink min-h-screen">
            <SEO path={`/legal/${slug}`} title={`${title} | Verde 2050`} description={description} />
            <GroupHeader />
            <section className="pt-40 pb-16 md:pb-20 bg-cream border-b border-hairline">
                <div className="max-w-4xl mx-auto px-6 md:px-12">
                    <p className="mono-label text-terracotta mb-8">Legal</p>
                    <h1 className="font-serif text-4xl md:text-5xl text-forest leading-[1.05] tracking-tight">
                        {title}
                    </h1>
                    <p className="mt-6 mono-label text-muted_text">Last updated: {lastUpdated || "pending"}</p>
                </div>
            </section>
            <article className="py-20 md:py-24">
                <div className="max-w-3xl mx-auto px-6 md:px-12 space-y-10">
                    {sections.map((s, i) => (
                        <section key={i} data-testid={s.testid || undefined}>
                            {s.heading && <h2 className="font-serif text-2xl text-forest mb-4">{s.heading}</h2>}
                            <div className="text-base text-forest leading-relaxed space-y-4">
                                {(s.paragraphs || []).map((p, j) => (
                                    <p key={j} className={s.placeholder ? "italic text-muted_text" : ""}>{p}</p>
                                ))}
                                {s.list && (
                                    <ul className="list-disc pl-6 space-y-2 marker:text-terracotta">
                                        {s.list.map((li, k) => (
                                            <li key={k}>
                                                {li.term && <strong className="font-semibold text-forest">{li.term}. </strong>}
                                                {li.text}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </section>
                    ))}
                </div>
            </article>
            <GroupFooter />
        </div>
    );
}

// ---- Privacy ----------------------------------------------------------------
const PRIVACY_SECTIONS = [
    {
        heading: "Who we are",
        paragraphs: [
            "This site is operated by Verde Partner Capital B.V. (\u201CVerde 2050\u201D, \u201Cwe\u201D, \u201Cus\u201D), a Netherlands\u2011registered company based in Cascais, Portugal. For any question about this policy or your personal data, write to hello@verde2050.com.",
            "For the purposes of the EU General Data Protection Regulation (GDPR) and the UK GDPR, Verde 2050 is the controller of the personal data described below.",
        ],
    },
    {
        heading: "What this policy covers",
        paragraphs: [
            "This policy describes what personal data we collect through verde2050.com, why we collect it, who we share it with, how long we keep it, and what rights you have. It applies to the inquiry form on /contact, the project information request on each project page, and the cookies described in our Cookie Notice.",
        ],
    },
    {
        heading: "What we collect and why",
        paragraphs: [
            "We only collect what you volunteer through one of the forms below. We do not sell personal data. We do not use it for advertising. We do not profile.",
        ],
        list: [
            {
                term: "Inquiry form (/contact) and project information requests (/projects/…)",
                text: "Your name, email, company and phone (optional), the inquiry type you selected, your message, and any documents you choose to attach (for example plot plans). You must tick a consent box confirming you have read this Privacy Policy. Lawful basis: taking steps at your request prior to entering into a commercial relationship, and our legitimate interest in responding to enquiries about our business.",
            },
            {
                term: "Land submission form (/sell-your-land and /pt/vender-terreno)",
                text: "Your name, email and phone/WhatsApp, the land's location or map link, municipality, approximate area, asking price and classification (if you share them), your description, and any documents or photographs you choose to upload. Used only to assess the land and contact you about it. Lawful basis: taking steps at your request prior to entering into a contract, and your consent to be contacted. Documents are stored privately and accessible only to our team. You will receive a confirmation email.",
            },
            {
                term: "The Investor Circle (/investor-circle)",
                text: "Your first and last name, email, country of residence, the opportunities you are interested in, the indicative amount you might consider (non-binding, and never used to decide eligibility), the communications you chose, any note you add, and how you found us (campaign tags and referring page). We record when you acknowledged this policy and, separately, whether and when you consented to receive our emails, with the wording you agreed to. If you start but do not finish registering, we keep your first-step details to understand drop-off, but never email you marketing. Lawful basis: your request to join, and your consent for emails, which you can withdraw at any time via the unsubscribe link in every email. Joining is not an investment offer or commitment.",
            },
            {
                term: "Anonymous usage analytics (only with your consent)",
                text: "If you accept analytics cookies, we record anonymous events such as project information requests, form submissions and clicks to partner websites (Verde Capital, Taylor Brokers, Instagram). No name, email or IP address is stored with these events.",
            },
            {
                term: "Server\u2011side technical data",
                text: "When you use the site, our hosting provider automatically logs your IP address, user\u2011agent and the URL requested for security and abuse\u2011prevention. These logs are retained for up to 30 days and are not linked to any form you submit.",
            },
        ],
    },
    {
        heading: "Who we share it with",
        paragraphs: [
            "We share personal data only with the small set of processors listed below, all of which are bound by written agreements requiring them to protect it and use it only on our instructions.",
        ],
        list: [
            { term: "MongoDB (database)", text: "Every form submission is stored in our MongoDB database. Data is at rest in the same region as our application infrastructure." },
            { term: "Emergent Email Proxy (Resend)", text: "Used to send you the confirmation email that follows your submission and to notify the Verde 2050 team by email." },
            { term: "Telegram Bot API", text: "We receive an internal notification in a private Telegram channel when a new inquiry lands. The notification contains a short summary \u2014 typically your name and the type of enquiry \u2014 not your full submission." },
            { term: "GitHub (news feed only)", text: "The home page pulls the Get The Plot editions list from a public GitHub repository. No personal data is sent to GitHub in the process." },
        ],
    },
    {
        heading: "International transfers",
        paragraphs: [
            "Some of our processors are established outside the European Economic Area. Where personal data is transferred outside the EEA, we rely on Standard Contractual Clauses or an equivalent GDPR\u2011recognised safeguard. A copy of the relevant safeguard is available on request.",
        ],
    },
    {
        heading: "How long we keep it",
        list: [
            { term: "Inquiries and attached documents", text: "up to 24 months from your last contact with us, then deleted or fully anonymised." },
            { term: "Deal submissions", text: "up to 5 years from submission where the opportunity did not proceed, longer where it did and where our records or applicable law require it." },
            { term: "Investor\u2011materials requests", text: "for the duration of your relationship with the fund and, once that ends, for the period required by applicable financial\u2011services record\u2011keeping rules (typically 5\u201310 years)." },
            { term: "Investor Circle registrations", text: "while you remain a member of the Circle. Write to hello@verde2050.com at any time to have your details deleted; we review and remove inactive and unfinished registrations periodically." },
            { term: "Server logs", text: "up to 30 days." },
        ],
    },
    {
        heading: "Your rights",
        paragraphs: [
            "Subject to conditions in the GDPR, you have the right to (i) access the personal data we hold about you, (ii) have inaccurate data corrected, (iii) have data erased where we no longer have a lawful basis to keep it, (iv) restrict or object to processing, (v) receive your data in a portable format, and (vi) withdraw any consent you have given. Where we rely on our legitimate interests, you can object at any time and we will stop unless we can demonstrate compelling grounds to continue.",
            "To exercise any of these rights, write to hello@verde2050.com. We aim to respond within 30 days. You also have the right to lodge a complaint with a supervisory authority \u2014 in the Netherlands the Autoriteit Persoonsgegevens (autoriteitpersoonsgegevens.nl); in Portugal the Comissão Nacional de Proteção de Dados (cnpd.pt).",
        ],
    },
    {
        heading: "Security",
        paragraphs: [
            "The site is served over HTTPS. Access to submissions in the database is restricted to a small number of named personnel, over authenticated connections only. Documents you attach to an inquiry are stored in private object storage and are only accessible to authenticated administrators. If you become aware of a security issue, please write to hello@verde2050.com.",
        ],
    },
    {
        heading: "Cookies",
        paragraphs: [
            "We use a very small number of cookies and local-storage entries. See the Cookie Notice for detail.",
        ],
    },
    {
        heading: "Changes to this policy",
        paragraphs: [
            "We update this policy from time to time. Material changes will be reflected in the \u201CLast updated\u201D date at the top and, where appropriate, notified to you by email.",
        ],
    },
    {
        heading: "Interim policy",
        paragraphs: [
            "This policy is an interim version, drafted to reflect what the site actually does today. A counsel\u2011reviewed version will replace it. If you would like to see the reviewed version when it is published, write to hello@verde2050.com and we will send it.",
        ],
        placeholder: false,
    },
];

export function Privacy() {
    return (
        <LegalPage
            slug="privacy"
            title="Privacy Policy"
            description="How Verde 2050 collects, uses and protects personal information submitted through this website — including the inquiry and project information forms."
            lastUpdated="31 October 2026"
            sections={PRIVACY_SECTIONS}
        />
    );
}

// ---- Cookies ---------------------------------------------------------------
export function Cookies() {
    return (
        <LegalPage
            slug="cookies"
            title="Cookie Notice"
            description="Cookies and local storage used by verde2050.com."
            lastUpdated="25 February 2026"
            sections={[
                {
                    heading: "What we set",
                    paragraphs: [
                        "verde2050.com uses only what is necessary to run the site, plus optional first\u2011party analytics if you accept them. We do not use advertising or third\u2011party tracking cookies.",
                    ],
                    list: [
                        { term: "v2050_cookie_consent (local storage)", text: "Remembers whether you accepted or declined analytics, so we don't ask again. Strictly necessary." },
                        { term: "Anonymous analytics events (optional)", text: "Only if you click Accept. Records anonymous interactions such as project information requests and clicks to partner websites. Stored on our own servers; no third\u2011party analytics provider." },
                    ],
                },
                {
                    heading: "How to change your choice",
                    paragraphs: [
                        "Use \u201CCookie preferences\u201D in the footer of any page at any time, or clear this site's data in your browser settings.",
                    ],
                },
                {
                    heading: "Related documents",
                    paragraphs: [
                        "For the full picture of how we handle personal data, see the Privacy Policy.",
                    ],
                },
            ]}
        />
    );
}

// ---- Terms -----------------------------------------------------------------
export function Terms() {
    return <LegalPage slug="terms" title="Terms of Use"
        description="Terms governing use of the verde2050.com website."
        sections={[{ heading: "Overview", paragraphs: ["Terms of Use pending legal review."], placeholder: true }]} />;
}

// ---- Important Information -------------------------------------------------
export function ImportantInformation() {
    return <LegalPage slug="important-information" title="Important Information"
        description="No-offer language, co-investment risk statement and relationships between Verde 2050 and related businesses."
        sections={[
            { heading: "No offer", paragraphs: ["Nothing on this website constitutes an offer to sell securities, a solicitation or investment advice. Project descriptions are for information only and may change."] },
            { heading: "Co-investment", paragraphs: ["Participation alongside Verde 2050 in any project is subject to eligibility, documentation and applicable regulation. We do not promise returns, planning approvals or project timelines. Real estate development is illiquid and involves risk, including loss of capital. Planning and licensing outcomes depend on municipal decisions outside our control."] },
            { heading: "Related businesses", paragraphs: ["Verde Capital, Taylor Brokers and Nicoline Beerkens Interiors are separate businesses. Residential brokerage is carried out by Taylor Brokers; financing is provided by Verde Capital. Images of planned projects include architectural visualisations; they are illustrative only and do not depict completed buildings."] },
            { heading: "Jurisdiction", paragraphs: ["Full jurisdictional statement pending legal review."], placeholder: true },
        ]} />;
}
